import { useEffect, useState } from "react";
import { api, API, formatError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, RotateCcw, Plus, FileWarning } from "lucide-react";
import { STAMP_IMG, A4_W, A4_H, stampImgStyle } from "@/components/PDFPreviews";
import RichTextEditor from "@/components/RichTextEditor";

// Page metrics come from PDFPreviews so the editor, the customer's view and the wizard preview
// are all the same A4 sheet. Body height drives where one printed page ends and the next begins,
// so what admin sees as "page 2" is what comes out of the printer as page 2.

// The page break marker. Kept as a plain-text sentinel on its own line rather than any kind of
// markup, because it is a structural instruction (start a new sheet here), not something that
// belongs in the small HTML allow-list the body itself can carry (see below) — `\f` means exactly
// one thing to `core.docx_gen._generate_document_docx`.
const PAGE_BREAK = "\f";

const splitPages = (body) => (body || "").split(PAGE_BREAK);
const joinPages = (pages) => pages.join(PAGE_BREAK);

/**
 * Admin's document editor — the document itself is the editing surface.
 *
 * Admin types directly onto a page-shaped sheet, the way they would in a word processor, rather
 * than filling a form beside a preview. Multi-page documents get one sheet per page with explicit
 * page breaks; "Add page" appends another sheet.
 *
 * **WYSIWYG (`RichTextEditor`), not a raw-markup `<textarea>`.** This used to be a plain textarea
 * plus a toolbar that inserted literal `<h1>`/`<b>`/`<i>`/`<u>` tags on click, deliberately not
 * contentEditable — the risk that ruled contentEditable out was pasted content accumulating hidden
 * markup. `RichTextEditor` (built for the Template Catalog) already closes that exact risk: paste
 * is forced to plain text, and every edit is re-serialized through the same small allow-list before
 * it's ever trusted, never raw `innerHTML`. Reusing it here means admin sees the same bold/heading
 * text the customer and the PDF will, instead of the literal `<b>...</b>` a resolved document body
 * carries (see `core.templates_data.render_body` — every filled-in answer is wrapped in it).
 *
 * Edits are saved to `document_overrides.body`, never to the customer's `form_data` — their
 * original submission stays auditable (backend/LIFECYCLE.md "Who can edit the document").
 * Once a body is saved it is used verbatim: no further {{field}} substitution happens, which is
 * why the server resolves the tokens (and their bolding) before handing the text over.
 */
export default function AdminDocumentEditor({ tid, open, onOpenChange, onSaved }) {
  const [doc, setDoc] = useState(null);
  const [pages, setPages] = useState([""]);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Bumped whenever a page's *initial* content changes from outside the editor's own typing (a
  // fresh load, a revert, or a page being added/removed shifts every later page's array index) —
  // RichTextEditor remounts on this per its own contract, since it is otherwise uncontrolled after
  // mount. Never bumped from `setPage` itself, or every keystroke would reset the cursor.
  const [loadToken, setLoadToken] = useState(0);

  useEffect(() => {
    if (!open || !tid) return;
    setDoc(null); setDirty(false);
    api.get(`/admin/transactions/${tid}/document`).then(({ data }) => {
      setDoc(data);
      setPages(splitPages(data.resolved_body || ""));
      setLoadToken((k) => k + 1);
    }).catch((e) => toast.error(formatError(e) || "Could not load the document"));
  }, [tid, open]);

  const setPage = (i, text) => {
    setPages((p) => { const n = [...p]; n[i] = text; return n; });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/transactions/${tid}/document`, { body: joinPages(pages) });
      toast.success("Document saved");
      setDirty(false);
      onSaved?.();
      return true;
    } catch (e) {
      toast.error(formatError(e) || "Save failed");
      return false;
    } finally { setBusy(false); }
  };

  const saveAndDownload = async () => {
    // Save first — the .docx is rendered server-side from the stored body, so downloading without
    // saving would hand admin a file that doesn't match what they just typed.
    if (!(await save())) return;
    const tok = localStorage.getItem("dt_token");
    window.open(`${API}/admin/transactions/${tid}/document/download?_t=${tok}`, "_blank", "noopener");
  };

  const revert = async () => {
    if (!window.confirm("Discard your edits and go back to the generated document?")) return;
    setBusy(true);
    try {
      await api.put(`/admin/transactions/${tid}/document`, { body: "" });
      const { data } = await api.get(`/admin/transactions/${tid}/document`);
      setDoc(data); setPages(splitPages(data.resolved_body || "")); setDirty(false);
      setLoadToken((k) => k + 1);
      toast.success("Reverted to the generated document");
      onSaved?.();
    } catch (e) { toast.error(formatError(e) || "Revert failed"); }
    finally { setBusy(false); }
  };

  const t = doc?.transaction;
  const empty = doc && !pages.some((p) => p.trim());

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o && dirty && !window.confirm("You have unsaved changes. Close anyway?")) return;
      onOpenChange(o);
    }}>
      {/* Wide enough for a full-width A4 sheet plus the grey desk around it */}
      <DialogContent data-testid="admin-doc-editor" className="max-w-[920px] w-[96vw] max-h-[94vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-serif-display flex items-center gap-2">
            {doc?.template?.title || "Document"}
            {t && <span className="font-mono text-xs text-[#6B7280]">#{t.id.slice(0, 8)}</span>}
            {doc?.body_is_edited && <span className="text-[10px] uppercase tracking-widest text-[#B45309]">edited by admin</span>}
            {dirty && <span className="text-[10px] uppercase tracking-widest text-[#F27373]">unsaved</span>}
          </DialogTitle>
        </DialogHeader>

        {!doc ? (
          <div className="p-10 text-sm text-[#6B7280]">Loading document…</div>
        ) : (
          <div className="overflow-y-auto bg-[#E9EAEC] -mx-6 px-6 py-6 flex-1">
            {empty && (
              <div className="mx-auto mb-4 flex items-start gap-2 text-xs text-[#92400E] bg-[#FEF3C7] border border-[#F5A524]/40 rounded p-3" style={{ maxWidth: A4_W }}>
                <FileWarning className="w-4 h-4 shrink-0 mt-0.5"/>
                <span>
                  This template has no document body yet, so the page below starts blank. Type the
                  document here — what you write is what gets printed and delivered.
                </span>
              </div>
            )}
            {pages.map((text, i) => (
              <div key={i} className="mx-auto mb-6" style={{ width: A4_W }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#6B7280]">Page {i + 1} of {pages.length}</span>
                  {pages.length > 1 && (
                    <button type="button" data-testid={`doc-remove-page-${i}`}
                      onClick={() => {
                        setPages((p) => p.filter((_, j) => j !== i));
                        setLoadToken((k) => k + 1); // later pages shift down an index — remount them with their own text, not the page that used to sit there
                        setDirty(true);
                      }}
                      className="text-[10px] text-[#F27373] hover:underline">Remove page</button>
                  )}
                </div>
                {/* The sheet. The stamp-paper image is a *viewing* affordance only, and — like the
                    customer's own DocumentSheet — belongs to page 1 alone: a real multi-page
                    document continues on ordinary plain paper behind it, so only the first sheet
                    reserves room for the pre-printed header band. It is never part of the saved
                    document; the downloaded .docx is body text alone, for printing onto real stamp
                    paper that already carries it (mirrors core/docx_gen.py's own top margin, itself
                    matched to core/pdf.py's TOP_MARGIN — see that module's docstring for why a
                    .docx can only approximate the PDF's per-page precision). */}
                {/* A4 is the *minimum* height, not a fixed one. It used to be `height` plus
                    `overflow-hidden`, which made the sheet look right and quietly swallowed any
                    text past the first page: the outer container scrolls between sheets, so an
                    overrun inside one sheet was clipped with no scrollbar and no way to reach it.
                    Admin could not read, let alone edit, the end of a long document.

                    Growing instead of clipping means nothing is ever hidden, there is still one
                    scrollbar (the outer one) rather than nested scrolling, and a page that has
                    outgrown its sheet is visibly too long — which is the cue to use Add page. */}
                <div className="bg-[#FEFDF8] shadow-[0_4px_24px_rgba(10,25,47,0.14)] border border-[#E8DFC7] flex flex-col"
                     style={{ minHeight: A4_H }}>
                  {i === 0 && (
                    <img src={STAMP_IMG} alt="Government of Tamil Nadu · Non-Judicial Stamp Paper"
                         className="w-full block select-none pointer-events-none shrink-0"
                         style={stampImgStyle} draggable={false}/>
                  )}
                  {/* No `min-h-0`: that overrides a flex item's default `min-height: auto` and is
                      precisely what let the editor be squeezed below its own content. `flex-1`
                      alone still fills a short page down to the sheet's minimum. */}
                  <div className="flex-1">
                    <RichTextEditor
                      testId={`doc-page-${i}`}
                      resetKey={`${loadToken}-${i}`}
                      value={text}
                      onChange={(v) => setPage(i, v)}
                      contentClassName="rounded-none border-0 focus:ring-0 px-14 py-10 text-[13px] leading-[1.85]"
                    />
                  </div>
                </div>
              </div>
            ))}
            <div className="mx-auto" style={{ maxWidth: A4_W }}>
              <Button variant="outline" size="sm" data-testid="doc-add-page"
                onClick={() => {
                  setPages((p) => [...p, ""]);
                  setLoadToken((k) => k + 1);
                  setDirty(true);
                }}
                className="bg-transparent border-white/15 text-white hover:bg-white/5">
                <Plus className="w-3.5 h-3.5 mr-1.5"/>Add page
              </Button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-3 border-t">
          <div className="text-[11px] text-[#6B7280]">
            The stamp header on page 1 is a preview only — the downloaded file is body text alone, as an editable .docx. Print positioning depends on the margins in whatever copy of Word opens it.
          </div>
          <div className="flex items-center gap-2">
            {doc?.body_is_edited && (
              <Button variant="ghost" size="sm" disabled={busy} onClick={revert} data-testid="doc-revert"
                className="text-[#B45309]"><RotateCcw className="w-3.5 h-3.5 mr-1.5"/>Revert</Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            <Button variant="outline" disabled={busy || !doc} onClick={save} data-testid="doc-save">
              {busy ? "Saving…" : "Save"}
            </Button>
            <Button disabled={busy || !doc} onClick={saveAndDownload} data-testid="doc-save-download"
              className="bg-[#0A192F] text-white hover:bg-[#112240]">
              <Download className="w-4 h-4 mr-1.5"/>Save &amp; Download (.docx)
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
